import pluralize from "pluralize";
import globalStore from "../../../../../state";
import { ActivityStatus, ActivityUIKind } from "../types";
import type { ActivityViewData } from "../types";

/**
 * Core-app globals used for generating target URLs and descriptions.
 *
 * These couple task type mapping to the core app's runtime environment
 * (globalStore, window.workspacesById). This is acceptable because
 * AsyncTaskService is only instantiated in the core app today.
 * If async tasks ever need to render in Rapid, these helpers would need
 * an abstraction similar to DatabaseAdapter.
 */
declare const window: any;

// --- Task type registry ---

interface TaskTypeMapping {
    icon: string;
    label: string;
    getDescription?: (params?: string, result?: string) => string | null;
    getTargetUrl?: (task: FirestoreTask) => string | null;
    openInNewTab: boolean;
}

export interface FirestoreTask {
    id: string;
    taskName: string;
    status: string;
    owner: string;
    progress?: { total: number; current: number };
    params?: string;
    result?: string;
    createdAt: any;
    updatedAt: any;
    completedAt?: any;
    failedAt?: any;
    parentTaskId?: string | null;
}

function getDataset(owner: string, datasetId: string) {
    try {
        const store = globalStore as any;
        return store.getState().workspaceDatasetsById?.[owner]?.[datasetId] || null;
    } catch {
        return null;
    }
}

function getDatasetUrl(owner: string, datasetId: string): string | null {
    try {
        const workspaceUrl = window.workspacesById?.[owner]?.url;
        if (!workspaceUrl) return null;
        const dataset = getDataset(owner, datasetId);
        if (!dataset?.url) return null;
        return `/${workspaceUrl}/${dataset.url}`;
    } catch {
        return null;
    }
}

/**
 * Opt-in registry of async task types shown in the activity center.
 *
 * Only tasks with an entry here are surfaced in the UI. To make a new
 * async task type visible, add a corresponding TaskTypeMapping entry.
 */
const taskTypes: Record<string, TaskTypeMapping> = {
    remapClasses: {
        icon: "far fa-exchange-alt",
        label: "Remap classes",
        openInNewTab: true,
        getTargetUrl: (task) => {
            try {
                const [, projectId] = JSON.parse(task.params || "[]");
                return getDatasetUrl(task.owner, projectId);
            } catch {
                return null;
            }
        }
    },
    mergeDatasets: {
        icon: "far fa-object-group",
        label: "Merge datasets",
        openInNewTab: true,
        getTargetUrl: (task) => {
            try {
                const [params] = JSON.parse(task.params || "[]");
                const targetUrl = params?.targetDataset?.url;
                if (!targetUrl) return null;
                const workspaceUrl = window.workspacesById?.[task.owner]?.url;
                return workspaceUrl ? `/${workspaceUrl}/${targetUrl}` : null;
            } catch {
                return null;
            }
        }
    },
    copyProjectImages: {
        icon: "far fa-copy",
        label: "Copy project images",
        openInNewTab: true,
        getDescription: (params) => {
            try {
                const [{ sourceDatasetId, destinationDatasetId, keepAnnotations }] = JSON.parse(
                    params || "[]"
                );
                const source = sourceDatasetId
                    ? getDataset(window.currentWorkspace, sourceDatasetId)
                    : null;
                const destination = destinationDatasetId
                    ? getDataset(window.currentWorkspace, destinationDatasetId)
                    : null;
                if (!source || !destination) return null;
                return `From ${source.name} to ${destination.name}${keepAnnotations ? " with annotations" : ""}`;
            } catch {
                return null;
            }
        },
        getTargetUrl: (task) => {
            try {
                const result = JSON.parse(task.result || "");
                return getDatasetUrl(task.owner, result?.destinationProjectId);
            } catch {
                return null;
            }
        }
    }
};

// --- Description helpers ---

function getMergeDescription(task: FirestoreTask): string | null {
    try {
        const [params] = JSON.parse(task.params || "[]");
        const count = params?.datasetIds?.length || 0;
        const targetName = params?.targetDataset?.name;
        if (!targetName) return null;
        return `${count} ${pluralize("dataset", count)} into ${targetName}`;
    } catch {
        return null;
    }
}

function getRemapDescription(task: FirestoreTask): string | null {
    try {
        const [, projectId, classesToDelete, remap] = JSON.parse(task.params || "[]");
        const dataset = getDataset(task.owner, projectId);
        const datasetName = dataset?.url || projectId;
        const parts: string[] = [];
        const remapKeys = Object.keys(remap || {});
        if (remapKeys.length)
            parts.push(`${remapKeys.length} renamed ${pluralize("class", remapKeys.length)}`);
        if (classesToDelete?.length)
            parts.push(
                `${classesToDelete.length} deleted ${pluralize("class", classesToDelete.length)}`
            );
        if (!parts.length) return null;
        return `${parts.join(", ")} on dataset ${datasetName}`;
    } catch {
        return null;
    }
}

// --- Conversion ---

function toStatusEnum(firestoreStatus: string): ActivityStatus {
    if (firestoreStatus === "created") return ActivityStatus.Pending;
    if (firestoreStatus === "running") return ActivityStatus.Running;
    if (firestoreStatus === "completed") return ActivityStatus.Completed;
    if (firestoreStatus === "failed") return ActivityStatus.Failed;
    return ActivityStatus.Pending;
}

function extractError(task: FirestoreTask): string | null {
    if (task.status !== "failed" || !task.result) return null;
    try {
        const parsed = JSON.parse(task.result);
        return parsed?.message || "Unknown error";
    } catch {
        return "Unknown error";
    }
}

function toDate(ts: any): Date {
    return ts.toDate();
}

function toDateOrNull(ts: any): Date | null {
    return ts ? ts.toDate() : null;
}

/** Converts a Firestore async_tasks document into an ActivityViewData. Returns null for unregistered task types. */
export function convertTask(task: FirestoreTask): ActivityViewData | null {
    const mapping = taskTypes[task.taskName];
    if (!mapping) return null;

    let description: string | null = null;
    if (task.taskName === "remapClasses") {
        description = getRemapDescription(task);
    } else if (task.taskName === "mergeDatasets") {
        description = getMergeDescription(task);
    } else if (mapping.getDescription) {
        description = mapping.getDescription(task.params, task.result);
    }

    return {
        id: task.id,
        kind: ActivityUIKind.AsyncTask,
        title: mapping.label,
        description,
        status: toStatusEnum(task.status),
        icon: mapping.icon,
        targetUrl: mapping.getTargetUrl ? mapping.getTargetUrl(task) : null,
        openInNewTab: mapping.openInNewTab,
        progress: task.progress?.total
            ? { current: task.progress.current || 0, total: task.progress.total }
            : null,
        error: extractError(task),
        createdAt: toDate(task.createdAt),
        completedAt: toDateOrNull(task.completedAt || task.failedAt)
    };
}
